import { RSAA } from 'redux-api-middleware';
import Queue from 'promise-queue';
import { CONST_FILE_UPLOAD } from '../../config/constants';
import { getMaxThreads } from '../features/selectors';
import endpoints from '../../utils/endpoints';
import { hasErrorData, hasConflictData } from '../../utils/benImportUtils';
import { MULTI_BENEFICIARY_CREATE_REQUEST } from '../../managePayee/states/MULTI_BENEFICIARY_REQUEST';
import { startBatchProcess, updateBatchProgress, stopBatchProcess } from './chunkingActions';

const { MAX_THREADS } = CONST_FILE_UPLOAD;
const SUBMIT_PAYLOAD_IN_CHUNKS_SUCCESS = 'SUBMIT_PAYLOAD_IN_CHUNKS_SUCCESS';
const SUBMIT_PAYLOAD_IN_CHUNKS_FAILURE = 'SUBMIT_PAYLOAD_IN_CHUNKS_FAILURE';

export const submitMultiPartChunkingRequest = (
	actionTypes,
	totalChunks,
	chunks,
	totalItems,
	isSingleUse = true,
	account = {}
) => {
	const isBeneficiaryRequest = actionTypes.includes(MULTI_BENEFICIARY_CREATE_REQUEST);
	return (dispatch, getState) => {
		const maxThreads = isSingleUse ? 1 : getMaxThreads(getState(), MAX_THREADS);

		const queue = new Queue(maxThreads);
		const getCompleted = () => {
			if (totalChunks === 1 || (queue.getQueueLength() === 0 && queue.getPendingLength() === 1)) {
				return totalChunks;
			} else if (maxThreads > 1 && queue.getPendingLength() > 1) {
				return totalChunks - (queue.getQueueLength() + 1);
			} else if (maxThreads > 1 && queue.getPendingLength() === 1) {
				return totalChunks - queue.getPendingLength();
			}
			return totalChunks - queue.getQueueLength();
		};

		dispatch(startBatchProcess(totalItems));
		dispatch(updateBatchProgress(0, totalChunks));

		let hasErrorsInChunkResponse = false;
		let meta = {
			allowedDuringApiCall: true,
		};
		if (isBeneficiaryRequest) {
			meta = {
				...meta,
				account,
				type: '',
				isPaymentFlow: isSingleUse,
			};
		}

		const flags = chunks.map(
			(chunkedPayments) =>
				new Promise((resolve, reject) => {
					const body = JSON.stringify({
						batch_list: chunkedPayments,
					});

					queue.add(() => {
						if (hasErrorsInChunkResponse) {
							return reject();
						}
						return dispatch({
							[RSAA]: {
								endpoint: endpoints.multipart.uri,
								method: 'POST',
								types: [
									actionTypes[0],
									{
										type: SUBMIT_PAYLOAD_IN_CHUNKS_SUCCESS,
									},
									SUBMIT_PAYLOAD_IN_CHUNKS_FAILURE,
								],
								body,
								headers: { 'x-bpi-version': endpoints.multipart.x_bpi_version },
								bailout: () => hasErrorsInChunkResponse,
							},
						}).then((response) => {
							// if no error in last chunk response
							if (!hasErrorsInChunkResponse) {
								// Update the loading flags
								const completed = getCompleted();
								dispatch(updateBatchProgress(completed, totalChunks));

								if (response.type !== SUBMIT_PAYLOAD_IN_CHUNKS_SUCCESS) {
									hasErrorsInChunkResponse = true;
									reject(response);
								}
								const benCreationValidation =
									isBeneficiaryRequest &&
									((isSingleUse && hasConflictData(response.payload.apiResponses)) ||
										hasErrorData(response.payload.apiResponses));
								const serverErrorValidation = hasErrorData(response.payload.apiResponses);
								if (
									// if payment file upload or has errors
									benCreationValidation ||
									serverErrorValidation
								) {
									hasErrorsInChunkResponse = true;
									reject(response);
								}
								const timeout = completed === totalChunks ? 250 : 0;
								setTimeout(() => {
									resolve(response);
								}, timeout);
							} else reject(response);
						});
					});
				})
		);

		return Promise.all(flags)
			.then((responses) => {
				// Flatten the responses into a single array
				const apiResponses = responses.reduce(
					(combined, response) => [...combined, ...response.payload.apiResponses],
					[]
				);
				return dispatch({
					type: actionTypes[1],
					payload: {
						apiResponses,
					},
					meta,
				});
			})
			.catch((responses) =>
				dispatch({
					type: actionTypes[2],
					meta,
					payload: responses.payload,
				})
			)
			.finally(() => dispatch(stopBatchProcess()));
	};
};
